import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { VariantProps, cva } from 'class-variance-authority';
import { MouseEventHandler, ReactNode, createContext, use } from 'react';
import { Drawer } from 'vaul';
import { button } from '../Button/base';
import { Icon } from '../Icon/Icon';
import { textVariants } from '../Text/Text';

export type BottomDrawerProps = {
  children?: ReactNode;
  title?: string;
};

const interactiveOutlineClassName =
  'rounded-xs hover:cursor-pointer focus-visible:outline-none focus-visible:ring focus-visible:ring-inset';

const bottomDrawerContentClassName = `bg-surface flex flex-col fixed bottom-0 left-0 right-0 rounded-t-pill pt-xxs pb-s px-xxs max-h-[90vh] border ${interactiveOutlineClassName}`;

const BottomDrawerPropsContext = createContext<{
  title?: string;
}>({
  title: '',
});

export const BottomDrawer = ({ children, title }: BottomDrawerProps) => {
  return (
    <BottomDrawerPropsContext value={{ title }}>
      {/* vaul's own focus-management prop, not the HTML autofocus attribute
          the lint rule guards against. vaul 1.x defaults it to false and
          preventDefault()s Radix's focus handler, which would leave focus
          stranded on the trigger behind the overlay; 0.9.x focused the drawer
          unconditionally. This restores that. */}
      {/* eslint-disable-next-line jsx-a11y-x/no-autofocus */}
      <Drawer.Root autoFocus>{children}</Drawer.Root>
    </BottomDrawerPropsContext>
  );
};

const BottomDrawerTrigger = ({
  children,
}: DialogPrimitive.DialogTriggerProps) => {
  return (
    <Drawer.Trigger aria-label="Open Bottom Drawer Menu" asChild>
      {children}
    </Drawer.Trigger>
  );
};

const BottomDrawerContent = (props: DialogPrimitive.DialogContentProps) => {
  const { title } = use(BottomDrawerPropsContext);

  const {
    children,
    onAnimationEnd,
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    ...restProps
  } = props;

  return (
    <Drawer.Portal>
      <Drawer.Overlay
        aria-label="Bottom Drawer Overlay"
        className="fixed inset-0 bg-overlay/40"
      />
      <Drawer.Content
        {...restProps}
        aria-modal
        className={bottomDrawerContentClassName}
        aria-describedby={ariaDescribedBy || undefined}
      >
        <div
          className={`flex items-center w-full ${
            title ? 'justify-between' : 'justify-end'
          }`}
        >
          {title ? (
            <Drawer.Title className="text-heading-s pl-m">{title}</Drawer.Title>
          ) : (
            <VisuallyHidden.Root asChild>
              <Drawer.Title>{ariaLabel}</Drawer.Title>
            </VisuallyHidden.Root>
          )}
          <Drawer.Close
            aria-label="Close Bottom Drawer"
            className={button({ intent: 'ghost', size: 'small' })}
          >
            <Icon name="close" size="large" />
          </Drawer.Close>
        </div>
        <div className="overflow-y-auto">{children}</div>
      </Drawer.Content>
    </Drawer.Portal>
  );
};

const bottomDrawerItem = cva('font-normal w-full px-m py-s text-left', {
  variants: {
    intent: {
      default: '',
      destructive: textVariants.color.contentNegative,
    },
  },
  defaultVariants: {
    intent: 'default',
  },
});

const BottomDrawerItem = (
  props: React.HTMLAttributes<HTMLDivElement | HTMLButtonElement> & {
    closeOnClick?: boolean;
    onClick?: MouseEventHandler;
    disabled?: boolean;
  } & VariantProps<typeof bottomDrawerItem>,
) => {
  const { closeOnClick, intent, disabled, ...restProps } = props;
  const itemClassName = bottomDrawerItem({ intent });
  const interactiveClassName = `hover:bg-hover-opacity active:bg-active-opacity ${interactiveOutlineClassName}`;

  // Neither `BottomDrawer.Close` nor a plain `div` is a native button, so a
  // `disabled` attribute would be inert here — render a non-interactive item
  // instead of relying on it, and drop `onClick`/`closeOnClick` so a disabled
  // item can neither fire its action nor dismiss the drawer.
  if (disabled) {
    const { onClick: _onClick, ...disabledProps } = restProps;
    return (
      <div
        {...disabledProps}
        aria-disabled="true"
        className={`${itemClassName} text-content-disabled cursor-not-allowed`}
      />
    );
  }

  return closeOnClick ? (
    <BottomDrawer.Close
      {...restProps}
      className={`${itemClassName} ${interactiveClassName}`}
    />
  ) : (
    <div {...restProps} className={itemClassName} />
  );
};

const BottomDrawerClose = (props: DialogPrimitive.DialogCloseProps) => {
  return <Drawer.Close className={interactiveOutlineClassName} {...props} />;
};

BottomDrawer.Trigger = BottomDrawerTrigger;
BottomDrawer.Content = BottomDrawerContent;
BottomDrawer.Item = BottomDrawerItem;
BottomDrawer.Close = BottomDrawerClose;
